import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Users,
  RefreshCw,
  Search,
  CheckCircle,
  Loader2,
  Mail,
  Clock,
  CloudUpload,
  Trash2,
} from "lucide-react";

interface Subscriber {
  id: string;
  email: string;
  first_name: string | null;
  confirmed: boolean;
  confirmed_at: string | null;
  created_at: string;
}

const BrevoContactsList = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Subscriber | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleDelete = async (sub: Subscriber) => {
    try {
      setDeletingEmail(sub.email);
      const { data, error } = await supabase.functions.invoke("newsletter-delete-contact", {
        body: { email: sub.email },
      });
      if (error) throw error;
      toast.success(`Contact supprimé : ${sub.email}`);
      setSubscribers((prev) => prev.filter((s) => s.id !== sub.id));
    } catch (e: any) {
      console.error(e);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeletingEmail(null);
      setConfirmDelete(null);
    }
  };

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("newsletter_subscribers")
        .select("id, email, first_name, confirmed, confirmed_at, created_at")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSubscribers((data as Subscriber[]) || []);
    } catch (error: any) {
      console.error("Erreur chargement abonnés:", error);
      toast.error("Erreur lors du chargement des abonnés");
    } finally {
      setLoading(false);
    }
  };

  const filtered = subscribers.filter((s) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      s.email.toLowerCase().includes(q) ||
      (s.first_name || "").toLowerCase().includes(q)
    );
  });

  const confirmedCount = subscribers.filter((s) => s.confirmed).length;
  const pendingCount = subscribers.length - confirmedCount;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Abonnés Newsletter
            </CardTitle>
            <CardDescription>
              {subscribers.length} abonnés au total · {confirmedCount} confirmés · {pendingCount} en attente
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  setSyncing(true);
                  const { data, error } = await supabase.functions.invoke("newsletter-sync-brevo");
                  if (error) throw error;
                  toast.success(`Synchronisation Brevo : ${data?.total ?? 0} contacts`);
                  await loadSubscribers();
                } catch (e: any) {
                  toast.error("Erreur synchronisation Brevo");
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing || loading}
              className="gap-2"
            >
              <CloudUpload className={`w-4 h-4 ${syncing ? "animate-pulse" : ""}`} />
              Synchroniser Brevo
            </Button>
            <Button
              variant="outline"
              onClick={loadSubscribers}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par email ou prénom..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : subscribers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Aucun abonné</p>
            <p className="text-sm">Les inscriptions à la newsletter apparaîtront ici</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Prénom</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Date inscription</TableHead>
                    <TableHead>Date confirmation</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          {s.email}
                        </div>
                      </TableCell>
                      <TableCell>{s.first_name || "-"}</TableCell>
                      <TableCell>
                        {s.confirmed ? (
                          <Badge className="bg-green-600 hover:bg-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Confirmé
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Clock className="w-3 h-3 mr-1" />
                            En attente
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(parseISO(s.created_at), "d MMM yyyy", { locale: fr })}
                      </TableCell>
                      <TableCell>
                        {s.confirmed_at
                          ? format(parseISO(s.confirmed_at), "d MMM yyyy", { locale: fr })
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setConfirmDelete(s)}
                          disabled={deletingEmail === s.email}
                        >
                          {deletingEmail === s.email ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="sm:hidden space-y-3">
              {filtered.map((s) => (
                <Card key={s.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm font-medium truncate flex-1">
                      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{s.email}</span>
                    </div>
                    {s.confirmed ? (
                      <Badge className="bg-green-600 text-[10px] ml-2">Confirmé</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] ml-2">En attente</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">
                    {s.first_name || "Prénom non renseigné"}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>Inscrit le {format(parseISO(s.created_at), "d MMM yyyy", { locale: fr })}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmDelete(s)}
                      disabled={deletingEmail === s.email}
                    >
                      {deletingEmail === s.email ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contact ?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.email} sera supprimé de la base de données, de Brevo et de Resend.
              Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default BrevoContactsList;
